import { useCallback, useEffect, useMemo, useState } from 'react';
import { ALL_NEW_TITLE, ALL_TITLES_RR, SOURCE } from '../../../core/constants';
import { getAllNewTitles } from '../columns/getAllNewTitles';
import { useBooksList, useDataRowCount } from '../../../core/modules/book/hooks';
import {
  Delete,
  ToCRM,
  Watch,
  BlockAuthor,
  ToPodiumTitle,
  ToContacted,
  MuteAuthor,
  Exclude
} from '../../../composed-components/Actions';
import { BASEFONTSIZE, PAGESIZE, REFETCHDELAY, RefetchOptions } from '../constants';
import { FilterForm, Settings, Sorting, SpinnerTabs } from '../../../composed-components';
import ResizeTable from '../../../composed-components/Resize';
import { EFontSize, TColumns } from '../types/types';
import Pagination from '../../../components/Pagination';
import { useFilter } from '../../../context/Filter.context';
import { useSort } from '../../../context/Sort.context';
import { useUserPermit } from '../../../context/User.context';
import Legend from '../../../composed-components/Legend';
import { TableFooter } from '../../../components/Table';
import { useActions } from '../../../context/Actions.context';
import getDataSource from '../utils/getDataSource';
import { applyRoleBasedFilters } from '../utils/roleBasedFilters';

const AllNewTitle = () => {
  const [fontSize, setFontSize] = useState<keyof typeof EFontSize>(BASEFONTSIZE);
  const [page, setPage] = useState<number>(1);
  const [deleteItems, setDeleteItems] = useState<string[]>([]);
  const [selectedOption, setSelectedOption] = useState<string>('All Titles');
  const [isFilterInitialized, setIsFilterInitialized] = useState<boolean>(false);

  const { source: sourcePath, isActionLoading, setActionLoading, isRefreshed } = useActions();
  const { edit_titles, move_titles_hubspot, role, default_filters: userDefaultFilters } = useUserPermit();
  const { filter, setFilter } = useFilter();
  const { sort } = useSort();

  const changePage = p => setPage(p);
  const deleteBooks = book => setDeleteItems(book);
  const changeFontSize = size => setFontSize(size);

  const isBookstat = sourcePath['source'].includes(SOURCE.bookstat);
  const TAB_ID = isBookstat ? ALL_NEW_TITLE : ALL_TITLES_RR;

  const { data = [], isLoading, isFetching, refetch, dataRefreshTime, isBackendJobComplete } = useBooksList(
    TAB_ID, {
      page,
      ...filter[TAB_ID],
      ...sort[TAB_ID]
    });

  const { count, refetch: refetchRowCount } = useDataRowCount(TAB_ID, { ...filter[TAB_ID] });

  const refetchTable = () => {
    setTimeout(() => {
      refetch();
      refetchRowCount();
      setActionLoading(false);
    }, REFETCHDELAY);
  };

  const sortComponent = useCallback(
    (title: string) => (
      <Sorting id={TAB_ID} title={title} changePage={changePage} />
    ),
    []
  );

  const thead = useMemo(() => {
    const columns = getAllNewTitles(sortComponent, TAB_ID);

    if (edit_titles) {
      columns.push({
        title: 'Actions',
        dataIndex: 'actions',
        key: 'actions',
        className: 'show td-actions',
        fixed: 'right',
        width: 90,
        render: (_, book) => (
          <div className='actions'>
            <Delete book={book} id={TAB_ID} />
            <BlockAuthor book={book} id={TAB_ID} />
            <MuteAuthor book={book} id={TAB_ID} />
            <Exclude book={book} id={TAB_ID} />
            <ToPodiumTitle book={book} id={TAB_ID} />
            <Watch book={book} id={TAB_ID} />
            <ToContacted book={book} id={TAB_ID} />
            {move_titles_hubspot && <ToCRM book={book} id={TAB_ID} />}
          </div>
        )
      });
    }

    return columns;
  }, [edit_titles, move_titles_hubspot, sortComponent]);

  const [columns, setColumns] = useState<TColumns[]>(thead);
  const dataSource = getDataSource(data, selectedOption, isBookstat ? 'bookstat' : 'royalroad');

  useEffect(() => {
    if (!isFilterInitialized) {
      if (!window.location.search) {
        const updatedFilters = applyRoleBasedFilters(filter, role, userDefaultFilters);

        setFilter(prevFilters => ({
          ...prevFilters,
          ...updatedFilters
        }));
      }

      setIsFilterInitialized(true);
    }
  }, []);

  useEffect(() => {
    if (!isRefreshed && isFilterInitialized) {
      refetch(RefetchOptions);
      refetchRowCount(RefetchOptions);
    }
    return () => {
      setSelectedOption('All Titles');
    };
  }, [page, filter[TAB_ID], sort[TAB_ID], isFilterInitialized]);

  return (
    <>
      <div className={'wrapper-controller'}>
        <FilterForm
          id={TAB_ID}
          page={page}
          changePage={changePage}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
          dataRefreshTime={dataRefreshTime}
          isBackendJobComplete={isBackendJobComplete}
        />
        <Settings
          thead={thead}
          id={TAB_ID}
          changeFontSize={changeFontSize}
          setColumns={setColumns}
          deleteBooks={deleteBooks}
          deleteItems={deleteItems}
        />
      </div>

      {!isLoading ? (
        <>
          <ResizeTable
            fontSize={fontSize}
            id={TAB_ID}
            dataSource={dataSource}
            deleteItems={deleteItems}
            isFetching={isFetching || isActionLoading}
            deleteBooks={deleteBooks}
            columns={columns}
            refetchTable={refetchTable}
          />
          <TableFooter>
            <Legend map={['crm', 'podium']} />
            <Pagination
              total={count}
              current={page}
              onChange={setPage}
              pageSize={PAGESIZE}
            />
          </TableFooter>
        </>
      ) : (
        <SpinnerTabs isLoading={isLoading} />
      )}
    </>
  );
};

export default AllNewTitle;